import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Bell, X } from "lucide-react";
import { useNotifications } from "@/components/notifications/useNotifications";
import NotificationItem from "@/components/notifications/NotificationItem";

/**
 * Bell button in the header. Owns the open/close UI + outside-click dismiss,
 * and delegates all data logic to the `useNotifications` hook.
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const { count, items, loadItems, markAllRead } = useNotifications(user);
  const [open, setOpen] = useState(false);
  const boxRef = useRef();

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    if (!open) await loadItems();
    setOpen((v) => !v);
  };

  if (!user) return null;

  return (
    <div ref={boxRef} className="relative">
      <button
        data-testid="notif-bell"
        onClick={toggle}
        className="relative h-9 w-9 grid place-items-center border border-[#2A2A2A] bg-black/60 hover:border-[#FF4500] transition-colors"
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span data-testid="notif-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#FF4500] text-black text-[10px] font-mono font-bold grid place-items-center">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-2 w-[340px] max-w-[92vw] bg-black border border-[#2A2A2A] shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2A]">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-400">Drop alerts</div>
            <div className="flex items-center gap-1">
              {count > 0 && (
                <button
                  data-testid="notif-mark-read"
                  onClick={markAllRead}
                  className="font-mono text-[10px] uppercase text-[#FF4500] hover:text-white"
                >
                  Read all
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[380px] overflow-y-auto divide-y divide-[#2A2A2A]">
            {items.map((n) => (
              <NotificationItem key={n.id} n={n} onClick={() => setOpen(false)} />
            ))}
            {items.length === 0 && (
              <div className="p-8 text-center font-mono text-[10px] uppercase tracking-widest text-gray-500">
                No drops yet — follow some artists!
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
